import { ChangeDetectionStrategy, Component, Input, OnInit, ViewChild, ViewEncapsulation } from "@angular/core";
import { ISplugDefinition, SplugDefinitionRemoteControllerService } from "core/api";
import { DynamicFormComponent } from "core/dynamic-form";
import { filterAllTruthyInTuple, mapKey, subjectFromObs } from "core/rx-js";
import { LoaderService } from "core/services";
import { SplugConfigService } from "core/splug";
import { DsgDeepTranslationKey } from "projects/designer/src/app/pipes/translate.pipe";
import { BehaviorSubject, Subject, combineLatest, distinctUntilChanged, filter, map, of, switchMap, take, takeUntil } from "rxjs";
import { UserSplugConfigService } from "../user-splug-config/user-splug-config.service";


@Component({
  selector: 'ss-user-splug-form',
  templateUrl: './user-splug-form.component.html',
  styleUrls: ['./user-splug-form.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UserSplugFormComponent implements OnInit {

  private readonly _destroy = new Subject<void>();

  @ViewChild(DynamicFormComponent)
  private _dynamicForm!: DynamicFormComponent

  readonly dynamicFormSubject = new BehaviorSubject<DynamicFormComponent>(undefined);

  readonly splugDefinitionSubject = new BehaviorSubject<ISplugDefinition>(undefined);
  @Input() get splugDefinition() {
    return this.splugDefinitionSubject.value;
  }
  set splugDefinition(value) {
    this.splugDefinitionSubject.next(value);
  }
  get validationKey() {
    return this.splugDefinition.id + '.validate'
  }

  readonly validationResultSubject = this._userSplugConfigService.validationResultSubject;

  readonly validationResultObs = combineLatest([
    this.splugDefinitionSubject,
    this.validationResultSubject
  ]).pipe(
    filterAllTruthyInTuple(),
    map(([splugDefinition, validationResult]) => validationResult[splugDefinition.id])
  );
  readonly selectedSplugsSubject = this._userSplugConfigService.selectedSplugsSubject;

  readonly isSelectedObs = combineLatest([this.selectedSplugsSubject, this.splugDefinitionSubject])
    .pipe(map(([selectedSplugs, splugDefinition]) => selectedSplugs[splugDefinition.id])
    )

  readonly validationMessageObs = combineLatest([this.validationResultObs, this.isSelectedObs])
    .pipe(
      map(([validationResult]) => (validationResult && this.isSelectedObs) ? validationResult.validationErrorMessage : '')
    )

  readonly validationColorObs = this.validationResultObs.pipe(filter(Boolean), mapKey('status'), map((status) => status === 'OK' ? 'lightgreen' : 'red'))

  readonly controlsObs = this.splugDefinitionSubject.pipe(
    filter(Boolean),
    map((splugDefinition) => splugDefinition.controls),
    filter(Boolean),
  )

  readonly splugIdSubject = subjectFromObs({
    initialValue: undefined,
    until: this._destroy,
    observable: this.splugDefinitionSubject.pipe(
      filter(Boolean),
      mapKey('id')
    )
  })

  readonly controlsSubject = subjectFromObs({
    initialValue: undefined,
    until: this._destroy,
    observable: this.splugDefinitionSubject.pipe(
      filter(Boolean),
      map(({ controls }) => controls))
  })


  readonly mainFormGroupObs = this.dynamicFormSubject.pipe(
    filter(Boolean),
    switchMap(dynamicForm => dynamicForm.mainFormGroupSubject),
    filter(Boolean)
  );

  readonly mainFormGroupSubject = subjectFromObs({
    until: this._destroy,
    observable: this.mainFormGroupObs,
    initialValue: null
  })
  get mainFormGroup() {
    return this.mainFormGroupSubject.value;
  }

  readonly splugFormGroup = this._userSplugConfigService.splugFormGroup;
  readonly initalSplugFormDataSubject = this._userSplugConfigService.initalSplugFormDataSubject;


  readonly initalFormDataObs = combineLatest([
    this.splugIdSubject,
    this.initalSplugFormDataSubject
  ])
    .pipe(
      filterAllTruthyInTuple(),
      map(([splug, initialFormData]) => initialFormData[splug]),
    )


  readonly formValidWithOneFieldObs = this.mainFormGroupSubject.pipe(
    filter(Boolean),
    distinctUntilChanged(),
    switchMap((mainFormGroup) => combineLatest([
      mainFormGroup.valueChanges,
      mainFormGroup.statusChanges,
      of(mainFormGroup)
    ])),
    map(([value, status, mainFormGroup]) => {


      if (!value) {
        return false;
      }

      if (Object.entries(value).filter(([dataField, value]) => value !== null && value !== undefined && value !== mainFormGroup.get(dataField)?.defaultValue).length === 0) {
        return false;
      }

      if (status !== 'VALID') {
        return false;
      }

      return true;
    }),
    distinctUntilChanged(),

  );




  constructor(
    private readonly _userSplugConfigService: UserSplugConfigService,
    private readonly _splugDefinitionRemoteControllerService: SplugDefinitionRemoteControllerService,
    private readonly _loaderService: LoaderService<DsgDeepTranslationKey>,
    private readonly _splugConfigService: SplugConfigService
  ) {

    combineLatest([
      this.mainFormGroupObs,
      this.splugDefinitionSubject
    ])
      .pipe(
        filterAllTruthyInTuple(),
        take(1)
      )
      .subscribe(([formGroup, splugDefinition]) => {
        this._userSplugConfigService.splugFormArray.push(formGroup)
        this.splugFormGroup[splugDefinition.id] = formGroup;
      })


    combineLatest([
      this.mainFormGroupObs,
      this.initalFormDataObs
    ])
      .pipe(
        filter(([mainFormGroup]) => Boolean(mainFormGroup)),
        takeUntil(this._destroy)
      )
      .subscribe(([formGroup, initialFormData]) => {

        if (!initialFormData) {
          formGroup.reset();
          return;
        }
        Object
          .entries(initialFormData)
          .forEach(([dataField, value]) => {
            formGroup.get(dataField).setValue(value)
          })
      })


    this.formValidWithOneFieldObs.pipe(takeUntil(this._destroy))
      .subscribe((validWithOneField) => {
        if (validWithOneField) {
          this._userSplugConfigService.selectSplug(this.splugDefinition.id)
          return;
        }

        this._userSplugConfigService.deselectSplug(this.splugDefinition.id)
      })
  }

  ngOnInit(): void {

    combineLatest([
      // this.initialSplugParamsSubject, 
      of(null),
      this.mainFormGroupSubject
    ])
      .pipe(
        filterAllTruthyInTuple(),
        takeUntil(this._destroy)
      )
      .subscribe(([initialFormData, form]) => {
        form.setValue(initialFormData);
      })

  }

  ngAfterViewInit() {

    this.dynamicFormSubject.next(this._dynamicForm)
  }


  ngOnDestroy() {

    const thisIdx = this._userSplugConfigService.splugFormArray.controls.indexOf(this.mainFormGroup!);
    this._userSplugConfigService.splugFormArray.removeAt(thisIdx);

  }


  validate() {

    const id = this._loaderService.show({ key: this.validationKey })
    const keyValuePairs = Object.entries(this.mainFormGroup.value)
      .map(([key, value]) => ({
        key,
        value: this._splugConfigService.executeStringifer(this.splugDefinition.id, key, value)
      }));

    this._splugDefinitionRemoteControllerService.validateSplugParams(this.splugDefinition.id, keyValuePairs)
      .pipe(
        this._loaderService.disposeLoader(id),
        take(1)
      )
      .subscribe((validationResult) => {
        const prev = { ...this._userSplugConfigService.validationResultSubject.value }
        this._userSplugConfigService.validationResultSubject.next({
          ...prev,
          [this.splugDefinition.id]: validationResult
        })
      })
  }

}
